import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import type { SalesPerformanceMetrics } from "./salesPerformanceTypes";

export interface SalesPerformanceViewProps {
  metrics: SalesPerformanceMetrics | null;
  isPending: boolean;
  error?: boolean | null;
}

/**
 * Shared presentation component for Sales Performance metrics.
 * Used by both production (server-side aggregated) and demo (in-memory) paths.
 */
export const SalesPerformanceView = ({
  metrics,
  isPending,
  error,
}: SalesPerformanceViewProps) => {
  if (isPending) {
    return (
      <Card className="w-full">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {Array.from({ length: 7 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full border-destructive/50 bg-destructive/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-destructive">
            Dati performance non disponibili
          </CardTitle>
          <CardDescription>
            Si è verificato un errore durante il caricamento dei dati di
            riepilogo commerciale. Riprova più tardi o contatta
            l'amministratore.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!metrics) {
    return null;
  }

  const { totals, salesBreakdown } = metrics;

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-bold">Sales Performance</CardTitle>
        <CardDescription>
          Riepilogo commerciale e avanzamento pipeline per agente
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* KPI Area */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div className="p-3 bg-muted/40 rounded-lg border text-center">
            <div className="text-xs text-muted-foreground font-medium">
              Aziende Totali
            </div>
            <div className="text-2xl font-bold mt-1">
              {totals.total_companies}
            </div>
          </div>
          <div className="p-3 bg-muted/40 rounded-lg border text-center">
            <div className="text-xs text-muted-foreground font-medium">
              Da Contattare
            </div>
            <div className="text-2xl font-bold mt-1">
              {totals.deals_to_contact}
            </div>
          </div>
          <div className="p-3 bg-muted/40 rounded-lg border text-center">
            <div className="text-xs text-muted-foreground font-medium">
              Pipeline Attiva
            </div>
            <div className="text-2xl font-bold mt-1 text-primary">
              {totals.active_pipeline}
            </div>
          </div>
          <div className="p-3 bg-muted/40 rounded-lg border text-center">
            <div className="text-xs text-muted-foreground font-medium">
              Interessato
            </div>
            <div className="text-2xl font-bold mt-1 text-blue-600 dark:text-blue-400">
              {totals.interested}
            </div>
          </div>
          <div className="p-3 bg-muted/40 rounded-lg border text-center">
            <div className="text-xs text-muted-foreground font-medium">
              Onboarding
            </div>
            <div className="text-2xl font-bold mt-1 text-amber-600 dark:text-amber-400">
              {totals.onboarding}
            </div>
          </div>
          <div className="p-3 bg-muted/40 rounded-lg border text-center">
            <div className="text-xs text-muted-foreground font-medium">
              Acquisito
            </div>
            <div className="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">
              {totals.acquired}
            </div>
          </div>
          <div className="p-3 bg-muted/40 rounded-lg border text-center">
            <div className="text-xs text-muted-foreground font-medium">
              Task Scaduti
            </div>
            <div className="text-2xl font-bold mt-1 text-destructive">
              {totals.overdue_tasks}
            </div>
          </div>
        </div>

        {/* Responsive Table with horizontal scroll container and min-width */}
        <div className="rounded-md border">
          <Table className="min-w-[640px]">
            <TableHeader>
              <TableRow>
                <TableHead>Commerciale</TableHead>
                <TableHead className="text-right">Aziende</TableHead>
                <TableHead className="text-right">Pipeline Attiva</TableHead>
                <TableHead className="text-right">Attività (7gg)</TableHead>
                <TableHead className="text-right">Task Scaduti</TableHead>
                <TableHead className="text-right">Interessato</TableHead>
                <TableHead className="text-right">Onboarding</TableHead>
                <TableHead className="text-right">Acquisito</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {salesBreakdown.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-muted-foreground py-4"
                  >
                    Nessun commerciale attivo trovato
                  </TableCell>
                </TableRow>
              ) : (
                salesBreakdown.map((row) => {
                  const displayName =
                    `${row.first_name || ""} ${row.last_name || ""}`.trim() ||
                    row.email ||
                    `Sales #${row.id}`;

                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">
                        <div>{displayName}</div>
                        <div className="text-xs text-muted-foreground">
                          {row.email}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {row.assigned_companies}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-primary">
                        {row.active_pipeline}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.recent_activities}
                      </TableCell>
                      <TableCell
                        className={`text-right ${
                          row.overdue_tasks > 0
                            ? "text-destructive font-semibold"
                            : "text-muted-foreground"
                        }`}
                      >
                        {row.overdue_tasks}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.interested}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.onboarding}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-emerald-600 dark:text-emerald-400">
                        {row.acquired}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
